import {NEW_SEARCH_WIDGET} from "../new-search-widget.consts";
import type {NewSearchWidgetController} from "../new-search-widget.controller";
import type {NewSearchWidgetState} from "../models/new-search-widget.state";
import {FetchHandler} from "../../../infrastructure/handlers/fetch-handler";
import {LocalStorageUtils} from "../../../infrastructure/utils/local-storage.utils";
import {EntitiesDomainService} from "../../../domain/entities/services/entities.domain-service";
import type {BaseApiRequestParams} from "../../../infrastructure/models/params/base-api-request-params";
import {EntitiesRequestParams} from "../../../domain/entities/models/entities.request-params";
import {LOCAL_STORAGE_KEYS} from "../../../core/consts";
import type {EntityDomainModel} from "../../../infrastructure/models/entity/entity.domain-model";
import {CompetitorViewModel} from "../../../domain/common/competitor/models/competitor-view-model";
import {CompetitionViewModel} from "../../../domain/common/competition/models/competition-view-model";
import {AthleteViewModel} from "../../../domain/common/athlete/athlete-view-model";
import {CompetitionDomainModel} from "../../../domain/common/competition/models/competition-domain-model";
import {CompetitorDomainModel} from "../../../domain/common/competitor/models/competitor-domain-model";
import {AthleteDomainModel} from "../../../domain/common/athlete/athlete-domain-model";
import type {EntityViewModel} from "../../../infrastructure/models/entity/entity.view-model";
import {CompetitorCompetitionsDomainModel} from "../../../domain/common/competitor/models/competitor-competitions.domain-model";
import {NewSearchWidgetSearchListModel} from "../models/new-search-widget.search-list.model";
import {NewSearchWidgetRecentSearchesEntityModel} from "../models/new-search-widget.recent-searches-entity.model";

export class NewSearchWidgetRecentSearchesFetchHandler extends FetchHandler {
  controller: NewSearchWidgetController;
  state: NewSearchWidgetState;
  localStorageUtils: LocalStorageUtils;

  type = NEW_SEARCH_WIDGET.ACTION_TYPES.FETCH.onRecentFetch;

  static injectables = [EntitiesDomainService, LocalStorageUtils];

  constructor(service: EntitiesDomainService, localStorageUtils: LocalStorageUtils) {
    super(service);

    this.localStorageUtils = localStorageUtils;
  }

  onLoaded() {
    super.onLoaded();

    this.action();
  }

  get requestParams(): BaseApiRequestParams {
    const recentSearches: Array<NewSearchWidgetRecentSearchesEntityModel> = this.recentSearches;
    const competitorsId: Array<Number> = this.getCompetitorsRecentSearchId(recentSearches);
    const competitionsId: Array<Number> = this.getCompetitionsRecentSearchId(recentSearches);
    const athletesId: Array<Number> = this.getAthletesRecentSearchId(recentSearches);

    const requestParams: EntitiesRequestParams = new EntitiesRequestParams(competitionsId, competitorsId, null, athletesId);
    return requestParams;
  }

  async action() {
    const dataSource = await super.fetch();
    const entities = this.sortRecentEntitiesByChronologicalOrder(dataSource.entities);

    this.state.updateRecentSearchListModel(new NewSearchWidgetSearchListModel(NEW_SEARCH_WIDGET.LIST_ID.recentSearches, entities, 'SELECTIONS_MENU_RECENT_SEARCHES', this.controller.terms, 'SELECTIONS_MENU_RECENT_SEARCHES_EMPTY_STATE', this.controller.device.isSmallView, true));
  }

  get recentSearches(): Array<NewSearchWidgetRecentSearchesEntityModel> {
    return this.localStorageUtils.get(LOCAL_STORAGE_KEYS.recentSearches) || [];
  }

  getCompetitorsRecentSearchId(recentSearches: Array<Object>): Array<Number>{
    const competitorsRecentSearchId: Array<Number> = recentSearches?.filter(entity => entity.type === CompetitorViewModel.name).map(competitor => competitor.id);

    return competitorsRecentSearchId;
  }

  getCompetitionsRecentSearchId(recentSearches: Array<Object>): Array<Number>{
    const competitionsRecentSearchId: Array<Number> = recentSearches?.filter(entity => entity.type === CompetitionViewModel.name).map(competition => competition.id);

    return competitionsRecentSearchId;
  }

  getAthletesRecentSearchId(recentSearches: Array<Object>): Array<Number>{
    const getAthletesRecentSearchId: Array<Number> = recentSearches?.filter(entity => entity.type === AthleteViewModel.name).map(athlete => athlete.id);

    return getAthletesRecentSearchId;
  }

  sortRecentEntitiesByChronologicalOrder(entities): Array<EntityDomainModel> {
    const recentSearches: Array<NewSearchWidgetRecentSearchesEntityModel> = this.recentSearches;
    const entitiesDM = recentSearches?.map(search => entities.find(entity => entity.id === search.id || entity.objectType === search.type));

    const entitiesVM: Array<EntityViewModel> = entitiesDM?.map(entity => {
      let entityVM = null;

      if(entity?.objectType === CompetitionDomainModel.name) {
        entityVM = new CompetitionViewModel(entity)
      } else if(entity?.objectType === CompetitorCompetitionsDomainModel.name || entity?.objectType === CompetitorDomainModel.name) {
        entityVM = new CompetitorViewModel(entity)
      } else if(entity?.objectType === AthleteDomainModel.name) {
        entityVM = new AthleteViewModel(entity)
      }

      return entityVM;
    });

    return entitiesVM.removeNulls();
  }
}
